import { CustomerServiceOutlined, ExclamationCircleOutlined, PushpinFilled } from '@ant-design/icons';
import { Avatar, Badge, Button, Dropdown, Tag, Tooltip } from 'antd';
import classNames from 'classnames';
import { IChat } from 'interfaces';
import React from 'react';
import classes from './ChatTab.module.scss';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import { useAppDispatch, useAppSelector } from 'store/hook';
import Assets from 'assets/icons';
import api from 'api';
import { setGlobalChats, setRoomAllChats, setSelectedChatRoom } from 'store/slices/chatsSlice';
import Spinner from 'components/Spinner/Spinner';
import { useNavigate, useParams } from 'react-router';
import { setSelectedDocument, setViewType } from 'store/slices/dataDocumentsSlice';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedChat: IChat;
  searchValue: string[];
  chat: IChat;
  onTabClick: () => void;
  markText: (
    text: string,
    markerRef: React.RefObject<HTMLDivElement>,
    timeout?: number
  ) => void;
};

const chatTypeIcon = {
  0: <Assets.FileIcon className={classes.iconElem} />,
  1: <Assets.MembersIcon className={classes.iconElem} />,
  3: <Assets.MemberIcon className={classes.iconElem} />,
  4: <Assets.TelegramIcon className={classes.iconElem} />,
  5: <Assets.MemberIcon className={classes.iconElem} />,
};

const statusCases = {
  sent: (
    <div className={classes.checked}>
      <div className={classes.check}></div>
    </div>
  ),
  pending: <div className={classes.checked}>
    <Spinner size={16} color='white' width='fit-content' />
  </div>,
  viewed: (
    <div className={classNames(classes.checked, classes.viewed)}>
      <div className={classes.check}></div>
      <div className={classes.check}></div>
    </div>
  ),
  rejected: (
    <div className={classes.checked}>
      <ExclamationCircleOutlined className={classes.rejected_amount} size={12} />
    </div>
  ),
};
function ChatTabItem({
  searchValue,
  chat,
  selectedChat,
  onTabClick,
  markText,
}: Props) {
  const [isDeletingChat, setIsDeletingChat] = React.useState(false);
  const [isOpenTip, setIsOpenTip] = React.useState(false);
  const [isDropdownActive, setIsDropdownActive] = React.useState(false);

  const { userData } = useAppSelector((state) => state.userData);
  const { roomAllChats, globalChats } = useAppSelector((state) => state.chat);
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { messageApi, isUploadingSomeChat } = useAppSelector((state) => state.windowState);

  const { chatId, room } = useParams();
  const { t } = useTranslation();
  
  const markerRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const lastMessageAuthor = chat?.last_message?.author?.id === userData?.id
    ? t('Chats.you')
    : chat?.last_message?.author?.name;
  
  const isCurrentChatActive = (!chatId && chat.entity_type === 1 && chat.entity_id === room && isUploadingSomeChat)
    || selectedChat?.id === chat.id || chatId === chat.id;  

  React.useEffect(() => {
    if (markText) {
      (searchValue.length ? searchValue : ['']).forEach((term) => {
        const text = term || '';
        markText(text, markerRef);
      });
    }
  }, [searchValue]);

  const onDeleteChat = async (e: any, chat: IChat) => {
    e.domEvent.stopPropagation();
    setIsDeletingChat(true);
    try {
      await api.deleteEmptyChat(chat.id);
      if (selectedChat?.id === chat?.id) {
        dispatch(setSelectedChatRoom(null));
      };

      if (chat.entity_type === 3) {
        if (selectedChat?.id === chat?.id) {
          navigate(`/global-chats/`);
        };
        dispatch(setGlobalChats(globalChats.filter((el) => el.id !== chat.id)));
      } else {
        if (selectedChat?.id === chat?.id) {
          navigate(`/room/${dataRoom?.id}/chat`);
          dispatch(setViewType('Table'));
          dispatch(setSelectedDocument(null));
        };
        dispatch(setRoomAllChats(roomAllChats.filter((el) => el.id !== chat.id)));
      };
      messageApi?.success(t('Chats.deletesSuccess'));
    } catch (e) {
      messageApi?.error(t('Chats.deletedError'));
    } finally {
      setIsDeletingChat(false);
    };
  };
  const chatType = t(`Chats.types.${chat.entity_type}`);
  const isSupportChat = chat.entity_type === 5;
  const chatEntity = !isSupportChat ? (chat.entity_name || chat.title) : '';
  const userEmail = chat.entity_type === 3 ? (chat.title === chatEntity ? '' : chat.title) : '';
  const avatarWord = chat.entity_type === 2 ? chat.title[0] : chatEntity[0];
  const lastMessage = chat.last_message?.content?.replaceAll(/\[link\]/gm, '').replaceAll(/\[\/link\]/gm, '');
  const extraTitle = chat.title && !isSupportChat && (chat.title !== userEmail && chat.title !== chat.entity_name) ? chat.title : ''

  return (
    <div
      className={classNames(
        classes.chatTab_item,
        isCurrentChatActive && classes.active,
        isSupportChat && classes.supportChat,
      )}
      onMouseLeave={() => setIsOpenTip(false)}
      onClick={onTabClick}
      key={chat.id}
      ref={markerRef}
    >
      <div className={classes.avatarWrap}>
        {isSupportChat ? (
          <div className={classes.supportAvatar}>
            <CustomerServiceOutlined className={classes.supportAvatarIcon} />
          </div>
        ) : (
          <Avatar size={'large'} className={classes.avatar}>
            <span className={classes.avatarWord}>{avatarWord}</span>
          </Avatar>
        )}
      </div>
      <div className={classes.chatTab_content}>
        <Tooltip
          placement='topLeft'
          open={isOpenTip && !isDropdownActive}
          onOpenChange={setIsOpenTip}
          mouseEnterDelay={1}
          mouseLeaveDelay={.5}
          title={`${chatType} ${chatEntity} ${userEmail} ${extraTitle}`}
        >
          <div className={classes.chatTab_title}>
            <div className={classes.chatTab_title_flex}>
              <div className={classes.chatName}>
                {chat.deleted_at && <Tag color='default' className={classes.tagDeleted}>{t('Chats.deletedFile')}</Tag>}
                {chatTypeIcon[chat.entity_type as keyof typeof chatTypeIcon]}
                {chatEntity || chatType}
              </div>
              <div className={classes.time}>
                {chat.last_message?.created_at &&
                  getDateWithTimeZone(chat.last_message?.created_at, `${userData?.date_format!}ChatList`, true)
                }
              </div>

              {isSupportChat && <PushpinFilled className={classes.pinnedIcon} />}
              {chat.last_message === null && chat.entity_type !== 1 && !isSupportChat && (
                isDeletingChat
                  ? <div className={classes.spinnerIcon}><Spinner size={18} /></div>
                  : <Dropdown
                    menu={{ items: [
                      {
                        key: 'delete',
                        className: classes.controlMenuItem,
                        label: (<span className={classes.spanItem}>
                          <Assets.TrashIcon /> {t('Users.HelperMenu.delete')}
                        </span>),
                        onClick: (e) => onDeleteChat(e, chat),
                      },
                    ] }}
                    open={isDropdownActive}
                    onOpenChange={setIsDropdownActive}
                    placement='topLeft'
                    classNames={{ root: classes.controlMenu }}
                  >
                    <Button
                      size='small'
                      onMouseEnter={() => setIsOpenTip(false)}
                      onMouseLeave={() => setIsOpenTip(true)}
                      onClick={(e) => e.stopPropagation()}
                      className={classes.iconBtn}
                      icon={<Assets.VerticalIcon /> }
                    />
                  </Dropdown>
              )}
            </div>
            <div className={classes.secondRow}>
              <div className={classes.innerInfo}>
                {chat?.last_message ? (
                  <>
                    <b style={{ fontWeight: 450 }}>
                      {lastMessageAuthor}:
                    </b>{' '}
                    <span className={classes.description}>
                      {' '}
                      {lastMessage && lastMessage}
                      {!lastMessage && chat.last_message?.attachment_ids?.length ? (
                        <span>{t('Chats.image')}</span>
                      ) : null}
                    </span>
                  </>
                ) : (
                  userEmail
                )}
              </div>

              <div className={classes.actions}>
                {chat.unviewed_amount && chat.unviewed_amount > 0 ? (
                  <Badge count={chat.unviewed_amount} className={classes.unviewed} overflowCount={99}/>
                ) : (
                  chat?.last_message?.status && chat?.last_message?.author?.email === userData?.email &&
                    statusCases[String(chat.last_message?.status) as keyof typeof statusCases]
                )}
              </div>
            </div>
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

export default ChatTabItem;
